import { defineStore } from 'pinia';
import { ref } from 'vue';
import { companySettingsApi } from '@/features/company-settings/api/companySettingsApi';

export interface PlanDialogData {
  currentPlan: string;
  planCd: string;
  baseEntities: number;
  existingAdditional: number;
  entityUsage: number;
  trialDaysRemaining: number | undefined;
  billingPeriod: string;
  billingEmail: string;
  billingContactName: string;
  prorationRemainingDays: number | null; // 증설 프로레이션: 잔여일
  prorationTotalDays: number | null;     // 증설 프로레이션: 총 기간일
  subscriptionEndDate: string | null;    // 현재 구독 종료일(갱신 후 만료일 미리보기용)
}

export interface PlanDialogOptions {
  renewalMode?: boolean;
  requiredEntities?: number;
}

export interface PlanDialogResult {
  success: boolean;
  payload?: {
    plan: string;
    duration: string;
    addEntities: number;
    amount: number;
    isMonthly: boolean;
  };
}

const defaultDialogData: PlanDialogData = {
  currentPlan: 'trial',
  planCd: '',
  baseEntities: 0,
  existingAdditional: 0,
  entityUsage: 0,
  trialDaysRemaining: undefined,
  billingPeriod: '',
  billingEmail: '',
  billingContactName: '',
  prorationRemainingDays: null,
  prorationTotalDays: null,
  subscriptionEndDate: null,
};

export const usePlanDialogStore = defineStore('planDialog', () => {
  const show = ref(false);
  const loading = ref(false);
  const renewalMode = ref(false);
  const requiredEntities = ref(0);
  const dialogData = ref<PlanDialogData>({ ...defaultDialogData });
  /** 결제 완료 시 증가 — HeaderPlanBadge 등이 watch하여 데이터 갱신 */
  const refreshKey = ref(0);

  let resolveCallback: ((value: PlanDialogResult) => void) | null = null;

  async function open(options?: PlanDialogOptions): Promise<PlanDialogResult> {
    if (show.value) return { success: false };

    loading.value = true;
    renewalMode.value = options?.renewalMode ?? false;
    requiredEntities.value = options?.requiredEntities ?? 0;

    try {
      const res = await companySettingsApi.getSubscriptionInfo();
      const info = (res as any).data?.data || (res as any).data;

      if (info) {
        dialogData.value = {
          currentPlan: info.currentPlan || 'trial',
          planCd: info.planCd || '',
          baseEntities: info.baseEntities || 0,
          existingAdditional: info.existingAdditional || 0,
          entityUsage: info.entityUsage || 0,
          trialDaysRemaining: info.trialDaysRemaining ?? undefined,
          billingPeriod: info.billingPeriod || '',
          billingEmail: info.billingEmail || '',
          billingContactName: info.billingContactName || '',
          prorationRemainingDays: info.prorationRemainingDays ?? null,
          prorationTotalDays: info.prorationTotalDays ?? null,
          subscriptionEndDate: info.subscriptionEndDate ?? null,
        };
      }
    } catch (err) {
      console.error('[PlanDialog] Failed to load subscription info:', err);
      loading.value = false;
      return { success: false };
    }

    loading.value = false;
    show.value = true;

    return new Promise<PlanDialogResult>((resolve) => {
      resolveCallback = resolve;
    });
  }

  /** 결제가 성공적으로 완료되었는지 추적 (onClose 시 갱신 트리거용) */
  let paymentCompleted = false;

  function onPlanUpdated(payload: PlanDialogResult['payload']) {
    resolveCallback?.({ success: true, payload });
    resolveCallback = null;
    paymentCompleted = true;
    // show.value는 false로 설정하지 않음 — UpdatePlanDialog가 Step 3(결과 화면)을
    // 보여준 뒤 사용자가 "Done" 클릭 시 update:modelValue(false)로 자연 닫힘 → onClose() 호출
  }

  function onClose() {
    resolveCallback?.({ success: false });
    resolveCallback = null;
    // 결제 완료 후 다이얼로그 닫힐 때 갱신 트리거 (이 시점이면 백엔드 데이터 반영 완료)
    if (paymentCompleted) {
      refreshKey.value++;
      paymentCompleted = false;
    }
    show.value = false;
  }

  return {
    show,
    loading,
    renewalMode,
    requiredEntities,
    dialogData,
    refreshKey,
    open,
    onPlanUpdated,
    onClose,
  };
});
